import React, { useContext, useState } from 'react';
import { FaTimes, FaSatellite, FaMapMarkedAlt, FaLeaf } from 'react-icons/fa';
import { ThemeContext } from '../App';
import { MercadoPagoService } from '../services/MercadoPagoService';
import { PAYMENT_CATALOG, formatCurrency, getAnnualSavings } from '../config/paymentCatalog';

/**
 * Modal de contratação do Add-on Sensoriamento Remoto (Satélite)
 *
 * Props:
 *  - show          {boolean}
 *  - onClose       {Function}
 *  - usuario       {Object}   dados do usuário logado
 *  - trialUsado    {boolean}  se já usou o trial
 *  - onTrialAtivado {Function} chamado após ativar trial com sucesso
 *  - statusAtual   {string}   'bloqueado' | 'expirado'
 */
export default function AddonSateliteModal({ show, onClose, usuario, trialUsado, onTrialAtivado, statusAtual }) {
  const { darkMode } = useContext(ThemeContext);
  const [periodicidade, setPeriodicidade] = useState('mensal');
  const [processando, setProcessando] = useState(false);
  const [ativandoTrial, setAtivandoTrial] = useState(false);

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  if (!show) return null;

  const precos = { mensal: PAYMENT_CATALOG.addon_satelite.monthly, anual: PAYMENT_CATALOG.addon_satelite.annual };
  const economiaAnual = getAnnualSavings('addon_satelite');

  const features = [
    { icon: '🛰️', texto: 'Imagens Sentinel-2 por talhão (ESA)' },
    { icon: '🌿', texto: 'Índice de vegetação NDVI e CIR' },
    { icon: '🗺️', texto: 'Visualização de imagens RGB no mapa' },
    { icon: '☁️', texto: 'Filtro por cobertura de nuvens' },
    { icon: '📅', texto: 'Histórico de imagens por período' },
    { icon: '📄', texto: 'Relatórios PDF com imagens de satélite' },
  ];

  const handleContratarCheckout = async () => {
    if (!usuario) return;
    setProcessando(true);
    try {
      await MercadoPagoService.abrirCheckout({
        plano: 'addon_satelite',
        periodicidade,
        usuario: {
          id: usuario.id,
          nome: usuario.user_metadata?.nome || usuario.email,
          email: usuario.email
        }
      });
    } catch (err) {
      console.error('Erro ao abrir checkout add-on satélite:', err);
      alert('Erro ao abrir pagamento. Tente novamente.');
    } finally {
      setProcessando(false);
    }
  };

  const handleAtivarTrial = async () => {
    if (!onTrialAtivado) return;
    setAtivandoTrial(true);
    try {
      const resultado = await onTrialAtivado();
      if (!resultado?.sucesso) {
        alert(resultado?.mensagem || 'Erro ao ativar trial.');
      }
    } finally {
      setAtivandoTrial(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex',
        alignItems: isMobile ? 'flex-end' : 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: isMobile ? 0 : 20
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: darkMode ? '#1e1e1e' : '#fff',
          borderRadius: isMobile ? '20px 20px 0 0' : 20,
          maxWidth: isMobile ? '100%' : 520,
          width: '100%',
          maxHeight: isMobile ? '95vh' : '90vh',
          overflow: 'auto',
          boxShadow: '0 10px 50px rgba(0,0,0,0.35)',
          color: darkMode ? '#f8fafc' : '#1e293b'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #7e57c2, #5e35b1)',
          color: '#fff',
          padding: isMobile ? '24px 20px 20px' : '28px 32px 24px',
          borderRadius: isMobile ? '20px 20px 0 0' : '20px 20px 0 0',
          position: 'relative',
          textAlign: 'center'
        }}>
          <button
            onClick={onClose}
            style={{
              position: 'absolute', top: 14, right: 14,
              background: 'rgba(255,255,255,0.2)', border: 'none',
              borderRadius: '50%', width: 32, height: 32,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: '#fff', fontSize: 16
            }}
          >
            <FaTimes />
          </button>

          <div style={{ fontSize: 48, marginBottom: 10 }}>🛰️</div>
          <h2 style={{ margin: 0, fontSize: isMobile ? 20 : 24, fontWeight: 900 }}>
            Add-on Satélite
          </h2>
          <p style={{ margin: '8px 0 0', fontSize: 14, opacity: 0.9 }}>
            {statusAtual === 'expirado'
              ? 'Seu acesso expirou. Renove para continuar usando.'
              : 'Monitore suas lavouras com imagens de satélite Sentinel-2.'}
          </p>
        </div>

        {/* Body */}
        <div style={{ padding: isMobile ? '20px 16px' : '24px 32px' }}>

          {/* Features */}
          <div style={{ marginBottom: 24 }}>
            {features.map((f, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                marginBottom: 12, fontSize: 14,
                color: darkMode ? '#cbd5e1' : '#475569'
              }}>
                <span style={{ fontSize: 18, flexShrink: 0 }}>{f.icon}</span>
                <span>{f.texto}</span>
              </div>
            ))}
          </div>

          {/* Seletor de periodicidade */}
          <div style={{
            background: darkMode ? '#2a2a2a' : '#f8fafc',
            borderRadius: 12, padding: 16, marginBottom: 20,
            border: darkMode ? '1px solid #374151' : '1px solid #e2e8f0'
          }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: darkMode ? '#94a3b8' : '#64748b' }}>
              ESCOLHA A PERIODICIDADE
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              {[
                { id: 'mensal', label: 'Mensal', preco: `${formatCurrency(precos.mensal)}/mês` },
                { id: 'anual', label: 'Anual', preco: `${formatCurrency(precos.anual)}/ano`, badge: `Economize ${formatCurrency(economiaAnual)}` }
              ].map(op => (
                <button
                  key={op.id}
                  onClick={() => setPeriodicidade(op.id)}
                  style={{
                    flex: 1, padding: '12px 8px', borderRadius: 10, cursor: 'pointer',
                    border: periodicidade === op.id ? '2px solid #7e57c2' : `2px solid ${darkMode ? '#374151' : '#e2e8f0'}`,
                    background: periodicidade === op.id ? (darkMode ? '#1e1535' : '#ede7f6') : 'transparent',
                    color: periodicidade === op.id ? '#7e57c2' : (darkMode ? '#94a3b8' : '#64748b'),
                    fontSize: 13, fontWeight: 700, textAlign: 'center', transition: 'all 0.15s'
                  }}
                >
                  <div>{op.label}</div>
                  <div style={{ fontSize: 15, fontWeight: 900, marginTop: 4 }}>{op.preco}</div>
                  {op.badge && (
                    <div style={{ fontSize: 11, color: '#22c55e', fontWeight: 700, marginTop: 3 }}>
                      {op.badge}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Botão contratar */}
          <button
            onClick={handleContratarCheckout}
            disabled={processando}
            style={{
              width: '100%', padding: '14px', borderRadius: 10, border: 'none',
              background: processando ? '#94a3b8' : 'linear-gradient(135deg, #7e57c2, #5e35b1)',
              color: '#fff', fontSize: 16, fontWeight: 700, cursor: processando ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              boxShadow: processando ? 'none' : '0 4px 14px rgba(94,53,177,0.4)',
              marginBottom: 12
            }}
          >
            {processando ? 'Aguarde...' : `Assinar por ${formatCurrency(precos[periodicidade])}/${periodicidade === 'mensal' ? 'mês' : 'ano'}`}
          </button>

          {/* Trial gratuito — só exibe se ainda não usou */}
          {!trialUsado && (
            <button
              onClick={handleAtivarTrial}
              disabled={ativandoTrial}
              style={{
                width: '100%', padding: '12px', borderRadius: 10,
                border: `2px solid ${darkMode ? '#374151' : '#e2e8f0'}`,
                background: 'transparent',
                color: darkMode ? '#94a3b8' : '#64748b',
                fontSize: 14, fontWeight: 600, cursor: ativandoTrial ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                marginBottom: 12
              }}
            >
              {ativandoTrial ? 'Ativando...' : '🎁 Experimentar grátis por 14 dias'}
            </button>
          )}

          <p style={{
            textAlign: 'center', fontSize: 12,
            color: darkMode ? '#6b7280' : '#94a3b8', margin: 0
          }}>
            Cancele cobranças futuras quando quiser • acesso mantido até o fim do ciclo pago
          </p>
        </div>
      </div>
    </div>
  );
}
